/**
 * What "Retry" repeats after a failed search, which failures earn one
 * automatic retry before the shopper sees anything, and the time bounds on a
 * search turn.
 *
 * Retry used to re-run the search from the top with the shopper's last words.
 * Inside a conversation that APPENDS those words as a new answer, so retrying
 * a failed shelf lookup sent the same answer to the model again: a second AI
 * turn, the same lookup, the same error (2026-10-03: six turns in 23 seconds
 * on one phone). Retry now repeats only the step that failed.
 */

/** Which step of the search failed. */
export type FailedSearchStage =
  /** The AI turn itself: the request never answered, or its stream broke. */
  | 'turn'
  /** The turn streamed fine; the page lookup after it could not be answered. */
  | 'shelf';

export type SearchRetryPlan =
  /** Run the lookup again for the turn on screen. No AI call, no new message. */
  | 'refetch-shelf'
  /** Ask the model again for the unanswered message already in the conversation. */
  | 'reload-turn'
  /** No conversation holds the words: submit them as a search. */
  | 'resubmit';

export function searchRetryPlan({
  stage,
  lastMessageRole,
}: {
  stage: FailedSearchStage | null;
  /** Role of the conversation's last message, if there is one. */
  lastMessageRole: string | undefined;
}): SearchRetryPlan {
  if (stage === 'shelf') return 'refetch-shelf';
  // useChat (ai 4.x) keeps the unanswered user message when a request fails,
  // so it is already the conversation's last message: regenerate for it.
  // Submitting the words again would append them a second time.
  return lastMessageRole === 'user' ? 'reload-turn' : 'resubmit';
}

/**
 * A request that never reached the server, as browsers report it: a TypeError
 * whose message is "Failed to fetch" (Chromium), "Load failed" (Safari) or
 * "NetworkError when attempting to fetch resource." (Firefox).
 *
 * Deliberately narrow, like isDeliberateRefusal. A refusal, a 5xx and a broken
 * stream all carry the response body or the SDK's own text as the message, are
 * not TypeErrors from fetch, and must reach the shopper rather than be retried
 * behind their back.
 */
export function isNetworkFailure(error: unknown): boolean {
  if (!(error instanceof TypeError)) return false;
  return /failed to fetch|load failed|networkerror/i.test(error.message);
}

/** Pause before the one automatic retry of a turn the network dropped. */
export const NETWORK_RETRY_DELAY_MS = 1000;

/** How long a turn's request may take to START answering (response headers). */
export const TURN_START_TIMEOUT_MS = 30_000;

/** How long a turn's stream may go without a new chunk before it is ended. */
export const TURN_STALL_TIMEOUT_MS = 30_000;

/**
 * Extra start time per byte of request body. A turn carrying a photo (up to
 * 3 MB) cannot be held to the same clock as a text turn on a slow uplink:
 * this allows for 25 KB/s, so two more minutes for the largest photo and a
 * second or two for an ordinary conversation.
 */
const UPLOAD_ALLOWANCE_MS_PER_BYTE = 1 / 25;

/**
 * `fetch` for a search turn, bounded in time from request to last chunk.
 *
 * Three failures never reached the search bar's error handling, because the
 * request either never settled or settled with something useChat does not
 * report. Each left the shopper's message on screen with nothing under it and
 * nothing to press:
 *   - the bot check wraps `fetch` and first waits for its challenge script. On
 *     a connection that cannot load it that wait has no end, and useChat's
 *     stop() cannot end it either: the abort signal is only looked at once the
 *     real request is made;
 *   - when that script fails to load outright, the wrapper rejects with a DOM
 *     `Event`. useChat calls onError only for an `Error`;
 *   - a phone that loses its connection mid-answer leaves the stream open and
 *     silent. It neither fails nor finishes.
 * All three now surface as an ordinary Error, through the ordinary onError.
 *
 * `globalThis.fetch` is read at CALL time on purpose: the bot check installs
 * its wrapper after page load, and the challenge header rides on that wrapper.
 * A reference captured at module load would send every turn without it.
 */
export function boundedTurnFetch({
  startTimeoutMs = TURN_START_TIMEOUT_MS,
  stallTimeoutMs = TURN_STALL_TIMEOUT_MS,
}: { startTimeoutMs?: number; stallTimeoutMs?: number } = {}): typeof fetch {
  return async (input, init) => {
    const controller = new AbortController();
    const abort = () => controller.abort();
    const outer = init?.signal;
    if (outer?.aborted) abort();
    else outer?.addEventListener('abort', abort, { once: true });

    const bodyBytes = typeof init?.body === 'string' ? init.body.length : 0;
    let timedOut = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<never>((_, reject) => {
      timer = setTimeout(() => {
        timedOut = true;
        abort();
        // Not a TypeError: half a minute of silence is not a dropped request,
        // and must not earn the silent automatic retry (isNetworkFailure).
        reject(new Error('The search did not answer in time.'));
      }, startTimeoutMs + bodyBytes * UPLOAD_ALLOWANCE_MS_PER_BYTE);
    });

    let response: Response;
    try {
      response = await Promise.race([globalThis.fetch(input, { ...init, signal: controller.signal }), timeout]);
    } catch (error) {
      outer?.removeEventListener('abort', abort);
      // An abort is recognised by name, as useChat does: it is the shopper
      // stopping the turn, and must stay an abort whatever object carries it.
      const isAbort = (error as { name?: unknown } | null)?.name === 'AbortError';
      if (timedOut || isAbort || error instanceof Error) throw error;
      throw new TypeError('Failed to fetch');
    } finally {
      if (timer !== undefined) clearTimeout(timer);
    }
    return endOnStall(response, stallTimeoutMs, abort);
  };
}

/** The same response, whose body errors when no chunk arrives for `stallTimeoutMs`. */
function endOnStall(response: Response, stallTimeoutMs: number, abort: () => void): Response {
  if (!response.body) return response;
  const reader = response.body.getReader();
  let timer: ReturnType<typeof setTimeout> | undefined;
  const body = new ReadableStream<Uint8Array>({
    async pull(streamController) {
      try {
        const chunk = await Promise.race([
          reader.read(),
          new Promise<never>((_, reject) => {
            timer = setTimeout(() => reject(new Error('The search stopped answering.')), stallTimeoutMs);
          }),
        ]);
        if (chunk.done) streamController.close();
        else streamController.enqueue(chunk.value);
      } catch (error) {
        abort();
        streamController.error(error);
      } finally {
        if (timer !== undefined) clearTimeout(timer);
      }
    },
    cancel(reason) {
      if (timer !== undefined) clearTimeout(timer);
      return reader.cancel(reason);
    },
  });
  return new Response(body, {
    status: response.status,
    statusText: response.statusText,
    headers: response.headers,
  });
}
